import * as v from 'valibot'

/** Inclusive [min, max] for each of the 5 cron fields, in order. */
const CRON_FIELD_RANGES: readonly [number, number][] = [
  [0, 59], // minute
  [0, 23], // hour
  [1, 31], // day of month
  [1, 12], // month
  [0, 7], // day of week (0 and 7 are both Sunday)
]

const parseUint = (value: string): number | null =>
  /^\d+$/.test(value) ? Number(value) : null

/** Validate a single comma-free cron term: `*`, `n`, `a-b`, or any of these with a `/step`. */
const isValidCronTerm = (term: string, min: number, max: number): boolean => {
  const parts = term.split('/')
  if (parts.length > 2) return false
  const [range = '', step] = parts
  if (step !== undefined) {
    const parsed = parseUint(step)
    if (parsed === null || parsed < 1) return false
  }
  if (range === '*') return true

  const bounds = range.split('-')
  if (bounds.length > 2) return false
  if (bounds.length === 2) {
    const lo = parseUint(bounds[0] ?? '')
    const hi = parseUint(bounds[1] ?? '')
    return lo !== null && hi !== null && lo >= min && lo <= hi && hi <= max
  }
  const single = parseUint(range)
  return single !== null && single >= min && single <= max
}

const isValidCronField = (field: string, range: [number, number]): boolean =>
  field.length > 0 &&
  field.split(',').every((term) => isValidCronTerm(term, range[0], range[1]))

/** True for a 5-field cron whose every field is within range (numeric syntax). */
const isValidCron = (value: string): boolean => {
  const fields = value.trim().split(/\s+/)
  return (
    fields.length === CRON_FIELD_RANGES.length &&
    fields.every((field, i) => isValidCronField(field, CRON_FIELD_RANGES[i]!))
  )
}

/**
 * 5-field cron expression (e.g. `"0 8 1 * *"` = 08:00 UTC on the 1st). Each
 * field is range-checked, so structurally-valid-but-impossible values like
 * `"99 99 99 99 99"` are rejected here rather than at the DO scheduler.
 */
export const cronExpressionSchema = v.pipe(
  v.string(),
  v.check(isValidCron, 'schedule must be a valid 5-field cron expression'),
)

interface SchedulableAgent {
  name: string
  listSchedules(criteria?: {
    type: 'scheduled' | 'delayed' | 'cron'
  }): Promise<{ id: string; callback: string }[]>
  cancelSchedule(id: string): Promise<boolean>
  schedule(when: Date | string | number, callback: string): Promise<unknown>
}

/**
 * Cancel every cron schedule bound to `callback`. Throws if any cancellation
 * fails so callers (e.g. `rescheduleCron`) don't add a replacement alongside a
 * schedule that couldn't be removed.
 */
export const cancelCron = async (
  agent: SchedulableAgent,
  callback: string,
): Promise<void> => {
  const existing = await agent.listSchedules({ type: 'cron' })
  const results = await Promise.all(
    existing
      .filter((s) => s.callback === callback)
      .map((s) => agent.cancelSchedule(s.id)),
  )
  if (results.some((cancelled) => !cancelled))
    throw new Error(`failed to cancel one or more "${callback}" cron schedules`)
}

/**
 * Replaces an agent's cron schedule for `callback` so a reconfigured cadence
 * takes effect immediately. Also persists `__ps_name` so PartyServer can
 * resolve the DO name in alarm handlers where older workerd versions don't
 * populate `ctx.id.name`. The agent's `ctx.storage` is passed separately
 * because `ctx` is protected on the Agent base class.
 */
export const rescheduleCron = async (
  agent: SchedulableAgent,
  storage: { put(key: string, value: string): Promise<void> },
  cron: string,
  callback: string,
): Promise<void> => {
  // Normalize whitespace so the scheduled value matches what was validated —
  // isValidCron is whitespace-lenient, but the downstream scheduler is not.
  const normalized = cron.trim().replace(/\s+/g, ' ')
  // Validate before any destructive work so an invalid cron can't leave the
  // agent with its existing schedules cancelled and no replacement.
  v.parse(cronExpressionSchema, normalized)

  await storage.put('__ps_name', agent.name)
  await cancelCron(agent, callback)
  await agent.schedule(normalized, callback)
}
